import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import classNames from "classnames";

// Components
import Portal from "../Portal";

import "./styles.sass";

type TooltipProps = {
  children: React.ReactNode;
  triggerRef: React.RefObject<HTMLElement>;
  isVisible: boolean;
  position?: "up" | "down";
  horizontalPosition?: "start" | "center" | "end";
};

const Tooltip = forwardRef<HTMLDivElement, TooltipProps>(
  (
    {
      children,
      triggerRef,
      isVisible,
      position = "up",
      horizontalPosition = "end",
    },
    ref,
  ) => {
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const tooltipRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(ref, () => tooltipRef.current as HTMLDivElement);

    useEffect(() => {
      if (triggerRef.current && isVisible && tooltipRef.current) {
        const triggerRect = triggerRef.current.getBoundingClientRect();
        const tooltipRect = tooltipRef.current.getBoundingClientRect();

        let top;
        let left;
        if (position === "up") {
          top = triggerRect.top - tooltipRect.height - 15;
        } else {
          top = triggerRect.bottom + 10;
        }

        switch (horizontalPosition) {
          case "start":
            left = triggerRect.left - 14;
            break;
          case "end":
            left = triggerRect.right - tooltipRect.width + 10;
            break;
          default:
            left =
              triggerRect.left +
              triggerRect.width / 2 -
              tooltipRect.width / 2 -
              2;
        }

        setTooltipPosition({ top, left });
      }
    }, [triggerRef, isVisible, position, horizontalPosition]);

    if (!isVisible) {
      return null;
    }

    const arrowClasses = classNames("Tooltip__arrow", {
      [`Tooltip__arrow-${position}`]: true,
      [`Tooltip__arrow-${horizontalPosition}`]: true,
    });

    return (
      <Portal>
        <div
          ref={tooltipRef}
          style={{
            top: `${tooltipPosition.top}px`,
            left: `${tooltipPosition.left}px`,
          }}
          className="Tooltip"
        >
          <div className="Tooltip__content">{children}</div>
          <div className={arrowClasses} />
        </div>
      </Portal>
    );
  },
);

export default Tooltip;
